import React, { FunctionComponent, useState, useEffect, useRef } from 'react';
import Button from 'components/UI/V3/Button/Button';
import { faPlus, faXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IntakeFormCustomQuestion, IntakeFormCustomQuestionCategory } from '@spike/marketplace-model';
import { v4 as uuid } from 'uuid';
import moment from 'moment';
import styles from './CustomQuestions.module.scss';
import { Switch } from 'components/UI/V3/Forms/Switch';
import { Textarea } from 'components/UI/V3/Forms/Textarea/Textarea';

interface CustomQuestionsProps {
    onEnable: (enabled: boolean) => void;
    onSave: (enabled: boolean, questions: IntakeFormCustomQuestion[], deletedIds: number[]) => void;
    initialQuestions?: IntakeFormCustomQuestion[];
    initialEnabled?: boolean;
    marketplaceId: number;
    attemptedSave?: boolean;
}

const DEFAULT_QUESTIONS = (marketplaceId: number): Array<IntakeFormCustomQuestion> => [
    {
        id: 0,
        order: 1,
        uuid: uuid(),
        enabled: true,
        required: true,
        marketplaceId,
        category: IntakeFormCustomQuestionCategory.FOR_CUSTOMERS,
        question: 'What are you looking for in a groomer?',
        createdAt: moment(),
        updatedAt: moment()
    },
    {
        id: 0,
        order: 2,
        uuid: uuid(),
        enabled: true,
        required: true,
        marketplaceId,
        category: IntakeFormCustomQuestionCategory.FOR_CUSTOMERS,
        question: 'What prompted you to look for a new groomer?',
        createdAt: moment(),
        updatedAt: moment()
    },
    {
        id: 0,
        order: 3,
        uuid: uuid(),
        enabled: true,
        required: true,
        marketplaceId,
        category: IntakeFormCustomQuestionCategory.FOR_CUSTOMERS,
        question: 'How did you hear about us?',
        createdAt: moment(),
        updatedAt: moment()
    },
    {
        id: 0,
        order: 4,
        uuid: uuid(),
        enabled: true,
        required: true,
        marketplaceId,
        category: IntakeFormCustomQuestionCategory.FOR_PETS,
        question: 'How often is your pet professionally groomed?',
        createdAt: moment(),
        updatedAt: moment()
    },
    {
        id: 0,
        order: 5,
        uuid: uuid(),
        enabled: true,
        required: true,
        marketplaceId,
        category: IntakeFormCustomQuestionCategory.FOR_PETS,
        question: 'Does your pet have any grooming-related fears or anxieties?',
        createdAt: moment(),
        updatedAt: moment()
    },
    {
        id: 0,
        order: 6,
        uuid: uuid(),
        enabled: true,
        required: true,
        marketplaceId,
        category: IntakeFormCustomQuestionCategory.FOR_PETS,
        question: 'Does your pet take medication or have any health conditions?',
        createdAt: moment(),
        updatedAt: moment()
    }
];

export const CustomQuestions: FunctionComponent<CustomQuestionsProps> = ({
    onEnable,
    onSave,
    initialQuestions = [],
    initialEnabled = false,
    marketplaceId,
    attemptedSave = false
}) => {
    const [enabled, setEnabled] = useState(initialEnabled);
    const [questions, setQuestions] = useState<IntakeFormCustomQuestion[]>(initialQuestions);
    const [deletedIds, setDeletedIds] = useState<number[]>([]);
    const [touchedQuestions, setTouchedQuestions] = useState<Set<number | string>>(new Set());
    const prevAttemptedSaveRef = useRef(attemptedSave);

    // Helper function to get unique question identifier
    const getQuestionId = (question: IntakeFormCustomQuestion): number | string | null => {
        return question.id || question.uuid || null;
    };

    // Validation functions
    const getInlineError = (text: string): string => {
        if (!text.trim()) return 'Question text is required';
        if (text.length > 255) return 'Question must be less than 255 characters';
        return '';
    };

    const getQuestionError = (index: number): string => {
        const question = questions[index];
        return question ? getInlineError(question.question) : '';
    };

    const shouldShowError = (index: number): boolean => {
        const question = questions[index];
        if (!question) return false;

        const questionId = getQuestionId(question);
        if (!questionId) return false;

        const isExistingQuestion = !!question.id;
        const isTouched = touchedQuestions.has(questionId);
        const hasError = getInlineError(question.question) !== '';

        // Existing questions: show error when touched
        // New questions: show error only after attempted save AND when touched
        if (isExistingQuestion) {
            return isTouched && hasError;
        }
        return attemptedSave && isTouched && hasError;
    };

    // Reset touched questions when drawer reopens (attemptedSave changes from true to false)
    useEffect(() => {
        const prevAttemptedSave = prevAttemptedSaveRef.current;
        prevAttemptedSaveRef.current = attemptedSave;

        if (prevAttemptedSave && !attemptedSave) {
            setTouchedQuestions(new Set());
        }
    }, [attemptedSave]);

    // Mark new empty questions as touched when user attempts to save
    useEffect(() => {
        if (attemptedSave) {
            setTouchedQuestions(prev => {
                const newTouched = new Set(prev);
                let changed = false;

                questions.forEach(question => {
                    const isNewEmpty = !question.id && !question.question.trim();
                    if (isNewEmpty && question.uuid) {
                        if (!newTouched.has(question.uuid)) {
                            newTouched.add(question.uuid);
                            changed = true;
                        }
                    }
                });

                return changed ? newTouched : prev;
            });
        }
    }, [attemptedSave, questions]);

    useEffect(() => {
        if (enabled && questions.length === 0) {
            const defaultQuestions = DEFAULT_QUESTIONS(marketplaceId);
            setQuestions(defaultQuestions);
        }
    }, [enabled, marketplaceId]);

    // Auto-save when questions change
    useEffect(() => {
        onSave(enabled, questions, deletedIds);
    }, [enabled, questions, deletedIds, onSave]);

    const handleToggle = (checked: boolean) => {
        setEnabled(checked);
        onEnable(checked);
    };

    const handleQuestionChange = (index: number, question: string) => {
        const updatedQuestions = [...questions];
        const currentQuestion = updatedQuestions[index];
        const questionId = getQuestionId(currentQuestion);

        // Mark question as touched when user interacts with it
        if (questionId) {
            setTouchedQuestions(prev => new Set(prev).add(questionId));
        }

        updatedQuestions[index] = {
            ...currentQuestion,
            question,
            updatedAt: moment()
        };
        setQuestions(updatedQuestions);
    };

    const handleCategoryChange = (index: number, category: IntakeFormCustomQuestionCategory) => {
        const updatedQuestions = [...questions];
        updatedQuestions[index] = {
            ...updatedQuestions[index],
            category,
            updatedAt: moment()
        };
        setQuestions(updatedQuestions);
    };

    const handleAddQuestion = () => {
        const newQuestion: IntakeFormCustomQuestion = {
            id: 0,
            order: questions.length + 1,
            uuid: uuid(),
            enabled: true,
            required: true,
            marketplaceId,
            category: IntakeFormCustomQuestionCategory.FOR_CUSTOMERS,
            question: '',
            createdAt: moment(),
            updatedAt: moment()
        };
        setQuestions([...questions, newQuestion]);
    };

    const handleDeleteQuestion = (index: number) => {
        const questionToDelete = questions[index];
        if (questionToDelete.id && questionToDelete.id > 0) {
            setDeletedIds([...deletedIds, questionToDelete.id]);
        }
        const updatedQuestions = questions.filter((_, i) => i !== index);
        setQuestions(updatedQuestions);
    };

    const showRequiredError = questions.length === 0 && attemptedSave;

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h3 className={styles.title}>
                    {showRequiredError ? (
                        <span className={styles.errorMessage}>Questions is required</span>
                    ) : (
                        'Custom Questions'
                    )}
                </h3>
                <Switch
                    checked={enabled}
                    onChange={checked => handleToggle(checked)}
                ></Switch>
            </div>

            {enabled && (
                <div>
                    <p className={styles.description}>
                        Create personalized questions to gather the information you need from clients & pets.
                    </p>

                    <div className={styles.questionsList}>
                        {questions.map((question, index) => (
                            <div
                                key={question.uuid || index}
                                className={styles.questionCard}
                            >
                                {/* Question Header */}
                                <div className={styles.questionHeader}>
                                    <button
                                        type="button"
                                        className={styles.deleteButton}
                                        onClick={() => handleDeleteQuestion(index)}
                                        aria-label="Delete question"
                                    >
                                        <FontAwesomeIcon icon={faXmark} />
                                    </button>
                                </div>

                                {/* Question Text */}
                                <Textarea
                                    label="Question"
                                    error={shouldShowError(index) ? getQuestionError(index) : ''}
                                    value={question.question}
                                    onChange={e => handleQuestionChange(index, e.target.value)}
                                    rows={3}
                                    maxLength={1000}
                                    placeholder="Enter your question here..."
                                    className={`${styles.questionInput} ${shouldShowError(index) ? styles.error : ''}`}
                                />

                                <div className={styles.categoryOptions}>
                                    <label className={styles.radioOption}>
                                        <input
                                            type="radio"
                                            name={`category-${index}`}
                                            checked={question.category === IntakeFormCustomQuestionCategory.FOR_PETS}
                                            onChange={() =>
                                                handleCategoryChange(index, IntakeFormCustomQuestionCategory.FOR_PETS)
                                            }
                                        />
                                        <span>For Pets</span>
                                    </label>
                                    <label className={styles.radioOption}>
                                        <input
                                            type="radio"
                                            name={`category-${index}`}
                                            checked={
                                                question.category === IntakeFormCustomQuestionCategory.FOR_CUSTOMERS
                                            }
                                            onChange={() =>
                                                handleCategoryChange(
                                                    index,
                                                    IntakeFormCustomQuestionCategory.FOR_CUSTOMERS
                                                )
                                            }
                                        />
                                        <span>For Clients</span>
                                    </label>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Add Question Button */}
                    <Button
                        label="Add Question"
                        size="inline"
                        variant="text-green"
                        onClick={handleAddQuestion}
                        leftIcon={faPlus}
                        className={styles.addButton}
                    />
                </div>
            )}
        </div>
    );
};

export default CustomQuestions;
